import { getDistanceUnitForMeasurementSystem } from '../../utils/measurementSystem';
import {
  MeasurementSystem,
  Distance,
  DistanceUnit,
  PageTotal,
  Duration,
  TimeUnit,
  FitnessApplicationActivityType,
} from '../../../types/__generated__';

export const MeasurementInMeters = 'meters';
export const MeasurementInFeet = 'feet';
export const MeasurementInSeconds = 'seconds';

const ActivityFilterComponentTypes = {
  ...FitnessApplicationActivityType,
  Any: 'ANY',
};

export const ActivityFilters: Record<string, ActivityFilter> = {
  all: {
    key: 'all',
    label: 'activity.totals.filter.all',
    componentTypes: [ActivityFilterComponentTypes.Any],
  },
  run: {
    key: 'run',
    label: 'activity.totals.filter.run',
    componentTypes: [FitnessApplicationActivityType.Run],
  },
  walk: {
    key: 'walk',
    label: 'activity.totals.filter.walk',
    componentTypes: [
      FitnessApplicationActivityType.Walk,
      FitnessApplicationActivityType.Hike,
    ],
  },
  ride: {
    key: 'ride',
    label: 'activity.totals.filter.ride',
    componentTypes: [FitnessApplicationActivityType.Ride],
  },
  swim: {
    key: 'swim',
    label: 'activity.totals.filter.swim',
    componentTypes: [FitnessApplicationActivityType.Swim],
  },
};

type FilteredTotals = {
  activityTotalsMap: ActivityTotalsMap;
} & ShouldReduceToSingleActivityResult;

export const buildFilteredTotals = (
  measurementSystem: MeasurementSystem,
  allTotals: PageTotal[],
): FilteredTotals => {
  const activityTotalsMap = buildActivityTotalsMap(
    measurementSystem,
    allTotals,
  );

  const { activityTypes, isSingleActivityMode } = shouldReduceToSingleActivity(
    Object.keys(activityTotalsMap),
  );

  return { activityTotalsMap, activityTypes, isSingleActivityMode };
};

const getDistanceInUnit = (
  measurementSystem: MeasurementSystem,
  distance: PageTotal,
): Distance => {
  const unit = getDistanceUnitForMeasurementSystem(measurementSystem);

  // The fitness and totals subsystem use alternative spellings of meter.
  const totalsUnit =
    unit === DistanceUnit.Metres ? MeasurementInMeters : unit.toLowerCase();
  const value = distance.amounts.find(a => a.unit === totalsUnit)?.value ?? 0;
  return {
    unit,
    value,
  };
};

type ActivityFilter = {
  key: string;
  label: string;
  componentTypes: string[];
};

type ActivityTotal = {
  distance: Distance;
  duration: Duration;
  elevation: Distance;
};
type ActivityTotalsMap = Record<string, ActivityTotal>;

const buildActivityTotalsMap = (
  measurementSystem: MeasurementSystem,
  allTotals: PageTotal[],
): ActivityTotalsMap => {
  const map = Object.keys(ActivityFilters).reduce(
    (map: ActivityTotalsMap, activityType: string) => {
      const filter = ActivityFilters[activityType];
      const total: ActivityTotal = {
        distance: sumComponents(
          filter,
          allTotals,
          measurementSystem,
          MeasurementTypeDistance,
        ) as Distance,
        duration: sumComponents(
          filter,
          allTotals,
          measurementSystem,
          MeasurementTypeElapsedTime,
        ) as Duration,
        elevation: sumComponents(
          filter,
          allTotals,
          measurementSystem,
          MeasurementTypeElevationGain,
        ) as Distance,
      };

      if (
        total.distance.value === 0 &&
        total.duration.value === 0 &&
        total.elevation.value === 0
      ) {
        return map;
      }

      map[activityType] = total;
      return map;
    },
    {},
  );

  return map;
};

const MeasurementTypeDistance = 'distance';
const MeasurementTypeElapsedTime = 'elapsed_time';
const MeasurementTypeElevationGain = 'elevation_gain';

type MeasurementType =
  | typeof MeasurementTypeDistance
  | typeof MeasurementTypeElapsedTime
  | typeof MeasurementTypeElevationGain;

const sumComponents = (
  filter: ActivityFilter,
  allTotals: PageTotal[],
  measurementSystem: MeasurementSystem,
  measurementType: MeasurementType,
): Distance | Duration => {
  const isDistance =
    measurementType === MeasurementTypeDistance ||
    measurementType === MeasurementTypeElevationGain;
  const value = filter.componentTypes.reduce(
    (activityTotal: number, type: string): number => {
      const targetDomain = `${type.toLowerCase()}:${measurementType}`;
      const total = allTotals.find(
        t => t.measurementDomain === targetDomain,
      ) ?? {
        measurementDomain: targetDomain,
        amounts: [],
      };

      if (isDistance) {
        const distance = getDistanceInUnit(measurementSystem, total);
        return distance.value + activityTotal;
      } else {
        const duration =
          total.amounts.find(a => a.unit === MeasurementInSeconds)?.value ?? 0;
        return duration + activityTotal;
      }
    },
    0,
  );

  return isDistance
    ? { unit: getDistanceUnitForMeasurementSystem(measurementSystem), value }
    : { unit: TimeUnit.Seconds, value };
};

type ShouldReduceToSingleActivityResult = {
  activityTypes: string[];
  isSingleActivityMode: boolean;
};

const shouldReduceToSingleActivity = (
  activityTypes: string[],
): ShouldReduceToSingleActivityResult => {
  const withoutAll = activityTypes.filter(x => x !== ActivityFilters.all.key);
  return withoutAll.length <= 1
    ? { activityTypes: withoutAll, isSingleActivityMode: true }
    : { activityTypes, isSingleActivityMode: false };
};
